import { Divider, HStack, Text, VStack } from "@chakra-ui/react";
import { InternalAdminUserFragment, UserAttributeFragment } from "gql/graphql";
import React, { memo, useMemo } from "react";
import colors from "app/colors";
import useInternalAdminUserAttributes from "../hooks/use-internal-admin-user-attributes";
import useInternalAdminUserAttributeCategoryList from "../hooks/use-internal-admin-user-attribute-category-list";

interface IProps {
  user: InternalAdminUserFragment;
}

const InternalAdminUserAttributes = ({ user }: IProps) => {
  const { userAttributes } = useInternalAdminUserAttributes(
    user.userAttributeIdList,
  );
  const { userAttributeCategories } = useInternalAdminUserAttributeCategoryList(
    {
      organizationId: user.organizationId,
      input: {
        limit: 100000,
        offset: 0,
      },
    },
  );

  const userAttributesByUserAttributeCategoryId = useMemo(
    () =>
      userAttributes.reduce(
        (acc, userAttribute) => {
          if (!userAttribute) return acc;
          if (userAttribute.userAttributeCategoryId) {
            acc[userAttribute.userAttributeCategoryId] = [
              ...(acc[userAttribute.userAttributeCategoryId] || []),
              userAttribute,
            ];
          }
          return acc;
        },
        {} as Record<string, UserAttributeFragment[]>,
      ),
    [userAttributes],
  );

  return (
    <VStack
      w="full"
      spacing={4}
      py={2}
      divider={<Divider />}
      alignItems="flex-start"
    >
      {userAttributesByUserAttributeCategoryId &&
        Object.keys(userAttributesByUserAttributeCategoryId).length === 0 && (
          <Text color={colors.gray50} fontSize={14} fontStyle="italic">
            No attributes assigned yet
          </Text>
        )}
      {userAttributeCategories.map((userAttributeCategory) => {
        const userUserAttributesInCategory =
          userAttributesByUserAttributeCategoryId?.[userAttributeCategory.id] ??
          [];
        if (!userUserAttributesInCategory.length) return null;
        return (
          <HStack spacing={0} w="full" key={userAttributeCategory.id}>
            <Text
              minW={140}
              color={colors.blackSecondary}
              fontWeight={700}
              fontSize={14}
            >
              {userAttributeCategory.name}
            </Text>
            <Text color={colors.gray50} fontWeight={500} fontSize={14}>
              {userUserAttributesInCategory
                .map((userAttribute) => userAttribute.name)
                .join(", ")
                .trim()}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
};

export default memo(InternalAdminUserAttributes);
